import { z } from 'zod'
import { UTCMillisecondsSchema } from '../time'
import { RoleDataSchema, RoleIdSchema } from '../role'
import { CompanyIdSchema, customIdSchema, isId, TeamIdSchema, UserIdSchema } from '../ids'
import { TinyTeamSchema, TinyUserSchema } from '../entity'
import { CompanyIndustrySchema, CompanyKindSchema, TinyCompanySchema } from '../company'
import { AuditHistorySchema } from '../common'

const FeatureTypeSchema = z.enum(['feature', 'config'])
export type FeatureType = z.infer<typeof FeatureTypeSchema>

export const FeatureIdSchema = customIdSchema('Feature', ':')
export type FeatureId = z.infer<typeof FeatureIdSchema>
export const isFeatureId = isId(FeatureIdSchema)

export const MILESTONES = ['Internal', 'MVP', 'Pilot', 'GA'] as const
export const MilestoneSchema = z.enum(MILESTONES)
export type Milestone = z.infer<typeof MilestoneSchema>

export const RolloutIssueIdSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9]*-[0-9]+$/, 'Use a Linear issue identifier such as TECH-1234, not a URL')
export type RolloutIssueId = z.infer<typeof RolloutIssueIdSchema>

export const TinyFeatureSchema = z.object({
  ...AuditHistorySchema.shape,
  featureId: FeatureIdSchema,
  featureType: FeatureTypeSchema.optional(),
  lastUsed: z.string().optional(),
  milestone: MilestoneSchema.optional(),
  rolloutIssueId: RolloutIssueIdSchema.optional().meta({
    description:
      'The Linear issue (identifier only, such as TECH-1234) that tracks ramping this flag or config value to everyone and then removing it',
    example: 'TECH-1234',
  }),
})
export type TinyFeature = z.infer<typeof TinyFeatureSchema>

export const FeatureNumberRangeFilterSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
})
export type FeatureNumberRangeFilter = z.infer<typeof FeatureNumberRangeFilterSchema>

export const FeatureValueSchema = z.union([z.boolean(), z.string(), z.number()])
export type FeatureValue = z.infer<typeof FeatureValueSchema>

export const FeatureRuleDataSchema = z
  .object({
    value: FeatureValueSchema.meta({
      description: 'Value when this rule is the first match: true or false for a flag',
    }),
    company: TinyCompanySchema.optional().meta({
      description: 'Matches this one company (copy it from feature get; only companyId is used)',
    }),
    user: TinyUserSchema.optional().meta({
      description: 'Matches this one user (copy it from feature get; only userId is used)',
    }),
    team: TinyTeamSchema.optional().meta({ description: 'Matches users on this team or a team under it' }),
    role: RoleDataSchema.optional().meta({ description: 'Matches users with this role' }),
    testGroups: z.array(z.number()).optional().meta({ description: 'Matches users whose test group is listed' }),
    isCompanyAdmin: z.boolean().optional().meta({ description: 'Matches users who are (or are not) company admins' }),
    isManager: z.boolean().optional().meta({ description: 'Matches users who are (or are not) managers' }),
    hasManager: z.boolean().optional().meta({ description: 'Matches users who have (or do not have) a manager' }),
    stage: z.string().optional().meta({
      description: 'Matches only in this deployment stage (for example `staging` or `production`)',
    }),
    accountAge: FeatureNumberRangeFilterSchema.optional().meta({
      description: 'Matches users whose first access was between min and max days ago',
    }),
    employedFor: FeatureNumberRangeFilterSchema.optional().meta({
      description: 'Matches users whose start date was between min and max days ago',
    }),
    betaProgram: z.boolean().optional().meta({ description: 'Matches users in (or not in) the beta program' }),
    releaseDate: z.number().optional().meta({
      description: 'Date rollout: matches only at or after this time (UTC epoch milliseconds)',
    }),
    expirationDate: z.number().optional().meta({
      description: 'Date rollout: matches only before this time (UTC epoch milliseconds)',
    }),
    companyKind: CompanyKindSchema.optional().meta({
      description: 'Matches every company of this kind, including companies created later',
    }),
    industry: CompanyIndustrySchema.optional().meta({
      description: 'Matches every company with this industry, including companies created later',
    }),
  })
  .meta({
    description:
      'One targeting rule. It matches when every condition it sets matches; a rule with only `value` matches everyone',
  })
export type FeatureRuleData = z.infer<typeof FeatureRuleDataSchema>

export const FeatureDataSchema = z.object({
  ...TinyFeatureSchema.shape,
  updatedAt: UTCMillisecondsSchema.optional().meta({
    description:
      'When the feature was last saved, in UTC epoch milliseconds. Send back the value you read on a full update; the update fails with a Conflict error if the feature changed since then.',
  }),
  rules: z.array(FeatureRuleDataSchema).meta({
    description:
      'Ordered targeting rules. They are evaluated top to bottom and the first matching rule decides the value; with no match the flag is off. The unconditional rule (only `value`) is the default and belongs last. An unconditional `false` rule first turns the flag off for everyone',
  }),
})
export type FeatureData = z.infer<typeof FeatureDataSchema>

export const FeatureRuleSchema = z.object({
  ...FeatureRuleDataSchema.omit({
    company: true,
    user: true,
    team: true,
    role: true,
  }).shape,
  companyId: CompanyIdSchema.optional(),
  userId: UserIdSchema.optional(),
  teamId: TeamIdSchema.optional(),
  roleId: RoleIdSchema.optional(),
})
export type FeatureRule = z.infer<typeof FeatureRuleSchema>
